import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Camera, ShieldCheck, Frown } from 'lucide-react';
import { ProfileService, IdentityProfileInput } from '@/core/application/profile/ProfileService';
import { OnboardingStorage } from '@/core/application/profile/useClinicalOnboarding';
import { ProfessionalProfileService } from '@/core/application/directory/ProfessionalProfileService';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

const formatFechaNacimiento = (text: string) => {
  const cleaned = text.replace(/\D/g, '');
  let formatted = cleaned;
  if (cleaned.length > 2) {
    formatted = `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
  }
  if (cleaned.length > 4) {
    formatted = `${formatted.slice(0, 5)}-${cleaned.slice(4, 8)}`;
  }
  return formatted;
};

const formatDni = (text: string) => {
  let formatted = text.toUpperCase().replace(/[\s.-]/g, '').replace(/[^VE\d]/g, '');
  if (formatted.length > 0 && /^\d/.test(formatted)) {
    formatted = 'V' + formatted;
  }
  return formatted;
};

const validateIdentityForm = (formData: IdentityProfileInput): string | null => {
  if (!formData.nombres_completos || !formData.apellidos_completos || !formData.perfil_dni || !formData.perfil_fecha_nacimiento) {
    return "Faltan campos obligatorios.";
  }
  const dateRegex = /^(0[1-9]|[12]\d|3[01])-(0[1-9]|1[012])-\d{4}$/;
  if (formData.perfil_fecha_nacimiento && !dateRegex.test(formData.perfil_fecha_nacimiento)) {
      return "La fecha de nacimiento debe tener el formato DD-MM-YYYY.";
  }
  return null;
};

const loadProfileData = async (
  setFormData: any,
  setIsVerified: any,
  setFetching: any
) => {
  try {
    const store = useProfileStore.getState();
    if (!store.hasChecked) {
      await store.fetchProfile();
    }
    const profile = useProfileStore.getState().profile;
    if (profile) {
      let displayDate = '';
      if (profile.perfil_fecha_nacimiento) {
        const [year, month, day] = profile.perfil_fecha_nacimiento.split('-');
        displayDate = `${day}-${month}-${year}`;
      }
      setFormData({
        nombres_completos: profile.nombres_completos || '',
        apellidos_completos: profile.apellidos_completos || '',
        perfil_dni: profile.perfil_dni || '',
        perfil_fecha_nacimiento: displayDate,
        avatar_url: profile.avatar_url || null,
      });
      setIsVerified(profile.perfil_identidad_verificada || false);
    }
  } catch (error) {
    console.error("Error cargando perfil:", error);
  } finally {
    setFetching(false);
  }
};

const uploadImageFile = async (
  file: File,
  formData: IdentityProfileInput,
  setFormData: any,
  setUploadingImage: any
) => {
  setUploadingImage(true);
  const oldAvatar = formData.avatar_url;
  const localUri = URL.createObjectURL(file);
  setFormData((prev: any) => ({ ...prev, avatar_url: localUri }));

  try {
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = (e: any) => resolve(e.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const base64Data = await base64Promise;

    const extension = file.name.split('.').pop() || 'jpg';
    const url = await ProfessionalProfileService.uploadAvatar(base64Data, extension);
    setFormData((prev: any) => ({ ...prev, avatar_url: url }));
  } catch (error) {
    console.error(error);
    globalThis.alert('No se pudo subir la foto a la nube.');
    setFormData((prev: any) => ({ ...prev, avatar_url: oldAvatar }));
  } finally {
    setUploadingImage(false);
  }
};

const pickAndUploadImage = (
  isVerified: boolean,
  formData: IdentityProfileInput,
  setFormData: any,
  setUploadingImage: any
) => {
  if (isVerified) return;
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.onchange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      uploadImageFile(file, formData, setFormData, setUploadingImage);
    }
  };
  fileInput.click();
};

const submitIdentityForm = async (
  formData: IdentityProfileInput,
  setHasAttemptedSubmit: any,
  setLoading: any,
  navigate: any
) => {
  setHasAttemptedSubmit(true);
  const validationError = validateIdentityForm(formData);
  if (validationError) {
    if (validationError !== "Faltan campos obligatorios.") {
      globalThis.alert(validationError);
    }
    return;
  }

  let dbDate = '';
  if (formData.perfil_fecha_nacimiento) {
    const [day, month, year] = formData.perfil_fecha_nacimiento.split('-');
    dbDate = `${year}-${month}-${day}`;
  }

  try {
    setLoading(true);
    await ProfileService.updateIdentity({
      ...formData,
      perfil_fecha_nacimiento: dbDate
    });
    await useProfileStore.getState().fetchProfile(true);
    globalThis.alert("¡Tu Identidad Soberana ha sido registrada con éxito!");
    
    const pendingCode = await OnboardingStorage.getPendingInvite();
    if (pendingCode) {
      navigate('/pro/become', { replace: true });
    } else {
      navigate('/hub', { replace: true });
    }
  } catch (err: any) {
    globalThis.alert(err.message);
  } finally {
    setLoading(false);
  }
};

const doLogoutHandler = async (
  setShowLogoutModal: any,
  navigate: any
) => {
  setShowLogoutModal(false);
  await supabase.auth.signOut();
  useProfileStore.getState().clearProfile();
  navigate('/consumer', { replace: true });
};

const FormInput = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  isVerified,
  hasAttemptedSubmit,
  maxLength
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  isVerified: boolean;
  hasAttemptedSubmit: boolean;
  maxLength?: number;
}) => {
  const isError = hasAttemptedSubmit && !value;
  return (
    <div style={styles.inputGroup}>
      <label htmlFor={id} style={styles.label}>{label} <span style={styles.required}>*</span></label>
      <input
        id={id}
        style={{
          ...styles.input,
          ...(isVerified ? styles.inputDisabled : {}),
          ...(isError ? styles.inputError : {})
        }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isVerified}
        maxLength={maxLength}
      />
    </div>
  );
};

export default function IdentityOnboardingScreen() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<IdentityProfileInput>({
    nombres_completos: '',
    apellidos_completos: '',
    perfil_dni: '',
    perfil_fecha_nacimiento: '',
    avatar_url: null,
  });

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    loadProfileData(setFormData, setIsVerified, setFetching);
  }, []);

  const handleDateChange = (text: string) => setFormData(prev => ({ ...prev, perfil_fecha_nacimiento: formatFechaNacimiento(text) }));
  const handleDniChange = (text: string) => setFormData(prev => ({ ...prev, perfil_dni: formatDni(text) }));
  const handlePickImage = () => pickAndUploadImage(isVerified, formData, setFormData, setUploadingImage);
  const handleSubmit = () => submitIdentityForm(formData, setHasAttemptedSubmit, setLoading, navigate);
  const doLogout = () => doLogoutHandler(setShowLogoutModal, navigate);

  if (fetching) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
      </div>
    );
  }

  const renderAvatarContent = () => {
    if (uploadingImage) return <div style={styles.spinnerSmall} />;
    if (formData.avatar_url) return <img src={formData.avatar_url} alt="Avatar" style={styles.avatarImage} />;
    return <User size={40} color="#94A3B8" />;
  };

  return (
    <div style={styles.screen}>
      <div style={styles.scrollContent}>
        <div style={styles.card}>
          <div style={styles.header}>
            <button 
              onClick={() => setShowLogoutModal(true)} 
              style={{ ...styles.iconBtn, alignSelf: 'flex-start', marginLeft: '-8px' }}
            >
              <LogOut size={28} color="#EF4444" />
            </button>
          </div>
          
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Dile al especialista quién eres</h1>
            <p style={styles.subtitle}>
              {isVerified 
                ? "Tu identidad ha sido verificada y asegurada criptográficamente. Ya no puede ser modificada." 
                : "Esta información es esencial para crear tu identidad y permitir que los profesionales te reconozcan."}
            </p>
          </div>

          <div style={styles.avatarContainer}>
            <button 
              onClick={handlePickImage} 
              disabled={uploadingImage || isVerified} 
              style={{ ...styles.avatarTouchable, cursor: (uploadingImage || isVerified) ? 'default' : 'pointer' }}
            >
              <div style={{ ...styles.avatarCircle, ...(formData.avatar_url ? {} : styles.avatarPlaceholder) }}>
                {renderAvatarContent()}
              </div>
              {!isVerified && (
                <div style={styles.editIconBadge}>
                  <Camera size={14} color="#FFF" />
                </div>
              )}
            </button>
            <span style={styles.avatarLabel}>
              {isVerified ? "Foto de perfil" : "Toca para subir tu foto"}
            </span>
          </div>

          <div style={styles.form}>
            <FormInput
              id="nombres"
              label="Nombres Completos"
              placeholder="Ej: Juan Carlos"
              value={formData.nombres_completos}
              onChange={(val) => setFormData(prev => ({ ...prev, nombres_completos: val }))}
              isVerified={isVerified}
              hasAttemptedSubmit={hasAttemptedSubmit}
            />

            <FormInput
              id="apellidos"
              label="Apellidos Completos"
              placeholder="Ej: Pérez Gómez"
              value={formData.apellidos_completos}
              onChange={(val) => setFormData(prev => ({ ...prev, apellidos_completos: val }))}
              isVerified={isVerified}
              hasAttemptedSubmit={hasAttemptedSubmit}
            />

            <FormInput
              id="cedula"
              label="Documento de Identidad (Cédula)"
              placeholder="Ej: V-12345678"
              value={formData.perfil_dni}
              onChange={(val) => handleDniChange(val)}
              isVerified={isVerified}
              hasAttemptedSubmit={hasAttemptedSubmit}
            />

            <FormInput
              id="fecha_nac"
              label="Fecha de Nacimiento"
              placeholder="DD-MM-YYYY"
              value={formData.perfil_fecha_nacimiento}
              onChange={(val) => handleDateChange(val)}
              isVerified={isVerified}
              hasAttemptedSubmit={hasAttemptedSubmit}
              maxLength={10}
            />
          </div>

          {!isVerified && (
            <button 
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div style={styles.spinnerWhite} />
              ) : (
                <>
                  <ShieldCheck size={20} color="#FFFFFF" style={{ marginRight: '8px' }} />
                  <span style={styles.buttonText}>Activar mi Perfil</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Custom Logout Modal */}
      {showLogoutModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalIconContainer}>
              <Frown size={48} color="#EF4444" />
            </div>
            <h2 style={styles.modalTitle}>¿Dejarnos tan pronto?</h2>
            <p style={styles.modalSubtitle}>
              Estás a un solo paso de culminar y acceder a las funcionalidades. Deberás iniciar sesión de nuevo si sales.
            </p>
            
            <button 
              style={styles.modalPrimaryBtn}
              onClick={() => setShowLogoutModal(false)}
            >
              <span style={styles.modalPrimaryBtnText}>Quedarme y completar perfil</span>
            </button>

            <button 
              style={styles.modalSecondaryBtn}
              onClick={doLogout}
            >
              <span style={styles.modalSecondaryBtnText}>Salir por ahora</span>
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input:focus { outline: 2px solid #0284C7; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  screen: { flex: 1, backgroundColor: '#F8FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  loadingContainer: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC', minHeight: '100vh' },
  scrollContent: { flexGrow: 1, padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' },
  card: { backgroundColor: '#FFFFFF', width: '100%', maxWidth: '500px', borderRadius: '24px', padding: '32px', boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.04)', border: '1px solid #F1F5F9', margin: '0 auto' },
  header: { marginBottom: '24px', display: 'flex', flexDirection: 'row' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
  titleSection: { marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: 800, color: '#0F172A', marginBottom: '8px', letterSpacing: '-0.5px', margin: '0 0 8px 0' },
  subtitle: { fontSize: '15px', color: '#64748B', lineHeight: '24px', margin: 0 },
  avatarContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' },
  avatarTouchable: { position: 'relative', marginBottom: '12px', background: 'none', border: 'none', padding: 0 },
  avatarCircle: { width: '110px', height: '110px', borderRadius: '55px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F1F5F9', overflow: 'hidden' },
  avatarPlaceholder: { border: '2px dashed #CBD5E1' },
  avatarImage: { width: '100%', height: '100%', objectFit: 'cover' },
  editIconBadge: { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#0284C7', width: '36px', height: '36px', borderRadius: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '3px solid #FFFFFF', boxShadow: '0px 4px 4px rgba(2, 132, 199, 0.3)' },
  avatarLabel: { fontSize: '14px', color: '#64748B', fontWeight: 500 },
  form: { width: '100%', marginBottom: '16px', display: 'flex', flexDirection: 'column' },
  inputGroup: { marginBottom: '20px', display: 'flex', flexDirection: 'column' },
  label: { fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '8px' },
  required: { color: '#EF4444' },
  input: { borderWidth: '1px', borderStyle: 'solid', borderColor: '#E2E8F0', borderRadius: '16px', padding: '16px', backgroundColor: '#F8FAFC', fontSize: '16px', color: '#0F172A', width: '100%' },
  inputError: { borderColor: '#EF4444', backgroundColor: '#FEF2F2' },
  inputDisabled: { backgroundColor: '#F1F5F9', color: '#94A3B8', borderColor: '#E2E8F0', cursor: 'not-allowed' },
  button: { backgroundColor: '#0284C7', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: '18px', borderRadius: '16px', marginTop: '16px', boxShadow: '0px 6px 12px rgba(2, 132, 199, 0.25)', border: 'none', cursor: 'pointer', width: '100%', transition: 'opacity 0.2s' },
  buttonDisabled: { backgroundColor: '#94A3B8', boxShadow: 'none', cursor: 'not-allowed' },
  buttonText: { color: '#FFFFFF', fontSize: '16px', fontWeight: 700 },
  
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px', zIndex: 1000 },
  modalContent: { backgroundColor: '#FFFFFF', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.1)' },
  modalIconContainer: { width: '80px', height: '80px', borderRadius: '40px', backgroundColor: '#FEF2F2', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px' },
  modalTitle: { fontSize: '24px', fontWeight: 800, color: '#0F172A', marginBottom: '12px', textAlign: 'center', margin: '0 0 12px 0' },
  modalSubtitle: { fontSize: '16px', color: '#64748B', textAlign: 'center', marginBottom: '32px', lineHeight: '24px', margin: '0 0 32px 0' },
  modalPrimaryBtn: { backgroundColor: '#0284C7', width: '100%', padding: '16px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px', border: 'none', cursor: 'pointer' },
  modalPrimaryBtnText: { color: '#FFFFFF', fontSize: '16px', fontWeight: 700 },
  modalSecondaryBtn: { backgroundColor: '#FFFFFF', width: '100%', padding: '16px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #E2E8F0', cursor: 'pointer' },
  modalSecondaryBtnText: { color: '#64748B', fontSize: '16px', fontWeight: 600 },
  
  spinner: { width: '40px', height: '40px', border: '4px solid #0284C7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerSmall: { width: '24px', height: '24px', border: '3px solid #0284C7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerWhite: { width: '24px', height: '24px', border: '3px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
